
import BaseInputOutlined from '@/components/inputs/BaseInputOutlined.vue'

import { computed, defineComponent, ref, onMounted, reactive } from 'vue'
import api from '../../../api/api'
import { useProject } from '../../../hooks/useProject'
import { useDateMenu } from '../../../hooks/useDateMenu'
import moment from 'moment'
import Breadcrumbs from '../../../components/Breadcrumbs.vue'
import SpinnerLoader from '../../../components/SpinnerLoader.vue'
import { mapFilterToObjectFormat, mapFilterFromObjectFormat, getFilterListFromObjectFormat } from '@/utils'
import { downloadFile } from '@/utils'
import CustomActivatorSelect from '../../../components/inputs/CustomActivatorSelect.vue'
import { Connector, Project, ProjectType } from '@/types/main'
import { CONNECTOR_TYPES } from '@root/src/vars/general'
import { getStaffTrajectoryReport } from '@root/src/api/chisai/GCB2'

interface TableHeader {
    text: string
    value: string
    align?: string
    sort?: (a: any, b: any) => number
    width?: string
    colspan?: number
    rowspan?: number
}

export default defineComponent({
    components: {
        BaseInputOutlined,
        Breadcrumbs,
        SpinnerLoader,
        CustomActivatorSelect,
    },
    name: 'StaffTrajectory',
    props: {},
    data: () => ({
        loading: false,
        dashboardData: [],
        filialIds: [] as null | string[],
        dateMenu: false,
        dates: [] as string[],
        dateFormat: 'DD.MM.YYYY',
        datePickerDateFormat: 'YYYY-MM-DD',
        sortState: {
            hairdressingSalon: { column: '', direction: 'none' },
            cosmetologySalon: { column: '', direction: 'none' },
            manicureSalon: { column: '', direction: 'none' },
            massageSalon: { column: '', direction: 'none' },
            epilationSalon: { column: '', direction: 'none' }
        },
        originalData: {
            hairdressingSalon: [],
            cosmetologySalon: [],
            manicureSalon: [],
            massageSalon: [],
            epilationSalon: []
        },
        hairdressingSalonTableData: [],
        cosmetologySalonTableData: [],
        manicureSalonTableData: [],
        massageSalonTableData: [],
        epilationSalonTableData: [],
        footerProps: { 'items-per-page-options': [10, 25, 50] },
    }),
    computed: {
        breadcrumbs(): any {
            return [
                {
                    text: this.project.name,
                    to: `/project/${this.project.id}`,
                },
                {
                    text: 'Отчет траектории сотрудников',
                    to: '',
                    disabled: true,
                },
            ]
        },
        project(): Project {
            return this.$store.getters.projectById(this.$router.currentRoute.params.id)
        },
        isHrProject(): boolean {
            return this.project.connectors.find((con) => con.connectorType === CONNECTOR_TYPES.GCB2)?.hrProject || false
        },
        GCB2Connector(): Connector | undefined {
            return this.project.connectors.find(
                connector => connector?.connectorType === CONNECTOR_TYPES.GCB2
            )
        },
        is4Hands(): boolean {
            return (this.GCB2Connector as any)?.is4H || false
        },
        projectType(): ProjectType {
            return this.$store.getters.projectType;
        },
        pseudoNetworkProjects(): Project[] {
            return this.$store.getters.pseudoNetworkProjects || []
        },
        networkProjects(): Project[] {
            return this.$store.getters.networkProjects || []
        },
        filialList(): any[] {
            if(this.projectType === 'pseudo_network') {
                return this.pseudoNetworkProjects?.filter(el => !el?.isMain) || []
            } else {
                return this.networkProjects?.filter(el => !el?.isMain) || []
            }
        },
        filialInputString(): string {
            if(this.filialIds.length === this.filialList.length) {
                return `Выбраны все (${this.filialIds.length})`
            } else {
                return `Филиалы(${this.filialIds.length})`
            }
        },
        dateRangeText(): string {
            const formatedDates = this.sortedDates.map(date =>
                moment(date, this.datePickerDateFormat).format(this.dateFormat)
            )
            const t = formatedDates.join(' - ')
            return formatedDates.join(' - ')
        },
        sortedDates(): string[] {
            return [
                ...this.dates.sort(
                    (a, b) =>
                        moment(a, this.datePickerDateFormat).unix() -
                        moment(b, this.datePickerDateFormat).unix()
                ),
            ]
        },
        hairdressingSalonTableHeaders(): TableHeader[] {
            return [
                { text: 'Название салона', value: 'hrProjectName', width: '100px', align: 'left' },
                {
                    text: 'Имя сотрудника',
                    value: 'hrStaffName',
                    width: '100px',
                    align: 'left'
                },
                {
                    text: 'Доля указаных услуг по категориям по количеству окрашивания/завивки',
                    value: 'hrColorCurlCntShare',
                    align: 'right',
                },
                {
                    text: 'Доля указаных услуг по категориям по количеству стрижки женские',
                    value: 'hrFHaircutCntShare',
                    align: 'right',
                },
                {
                    text: 'Доля указаных услуг по категориям по количеству стрижки мужские и детские',
                    value: 'hrMChHaircutCntShare',
                    align: 'right',
                },
                {
                    text: 'Доля указаных услуг по категориям по количеству укладки',
                    value: 'hrStylingCntShare',
                    align: 'right',
                },
                {
                    text: 'Доля указаных услуг по категориям по количеству уходы для волос',
                    value: 'hrHairstylingCntShare',
                    align: 'right',
                },
                {
                    text: 'Доля указаных услуг по категориям по количеству прочее',
                    value: 'hrOthersCntShare',
                    align: 'right',
                },
                {
                    text: 'Доля указаных услуг по категориям по количеству доля окрашиваний/завивок от женских стрижек (не менее 30%)',
                    value: 'hrColorCurlFHaircutCntShare',
                    align: 'right',
                },
                {
                    text: 'Среняя оценка по категориям оказаных услуг окрашивания/завивки',
                    value: 'hrColorCurlScore',
                    align: 'right',
                },
                {
                    text: 'Среняя оценка по категориям оказаных услуг стрижки женские',
                    value: 'hrFHaircutScore',
                    align: 'right',
                },
                {
                    text: 'Среняя оценка по категориям оказаных услуг стрижки мужские и детские',
                    value: 'hrMChHaircutScore',
                    align: 'right',
                },
                {
                    text: 'Среняя оценка по категориям оказаных услуг укладки',
                    value: 'hrStylingScore',
                    align: 'right',
                },
                {
                    text: 'Среняя оценка по категориям оказаных услуг уходы для волос',
                    value: 'hrHairstylingScore',
                    align: 'right',
                },
                {
                    text: 'Среняя оценка по категориям оказаных услуг прочее',
                    value: 'hrOthersScore',
                    align: 'right',
                },
            ]
        },
        cosmetologySalonTableHeaders(): TableHeader[] {
            return [
                { text: 'Название салона', value: 'hrProjectName', width: '100px', align: 'left' },
                {
                    text: 'Имя сотрудника',
                    value: 'hrStaffName',
                    width: '100px',
                    align: 'left'
                },
                {
                    text: 'Доля указаных услуг по категориям по количеству депиляция',
                    value: 'hrDepilationCntShare',
                    align: 'right',
                },
                {
                    text: 'Доля указаных услуг по категориям по количеству массаж',
                    value: 'hrMassageCntShare',
                    align: 'right',
                },
                {
                    text: 'Доля указаных услуг по категориям по количеству прокол',
                    value: 'hrPunctureCntShare',
                    align: 'right',
                },
                {
                    text: 'Доля указаных услуг по категориям по количеству макияж',
                    value: 'hrMakeupCntShare',
                    align: 'right',
                },
                {
                    text: 'Доля указаных услуг по категориям по количеству уход за бровями и ресницами',
                    value: 'hrEyebrowCareCntShare',
                    align: 'right',
                },
                {
                    text: 'Доля указаных услуг по категориям по количеству прочее',
                    value: 'hrOthersCntShare',
                    align: 'right',
                },
                {
                    text: 'Среняя оценка по категориям оказаных услуг депиляция',
                    value: 'hrDepilationScore',
                    align: 'right',
                },
                {
                    text: 'Среняя оценка по категориям оказаных услуг массаж',
                    value: 'hrMassageScore',
                    align: 'right',
                },
                {
                    text: 'Среняя оценка по категориям оказаных услуг прокол',
                    value: 'hrPunctureScore',
                    align: 'right',
                },
                {
                    text: 'Среняя оценка по категориям оказаных услуг макияж',
                    value: 'hrMakeupScore',
                    align: 'right',
                },
                {
                    text: 'Среняя оценка по категориям оказаных услуг уход за бровями и ресницами',
                    value: 'hrEyebrowCareScore',
                    align: 'right',
                },
                {
                    text: 'Среняя оценка по категориям оказаных услуг прочее',
                    value: 'hrOthersScore',
                    align: 'right',
                },
            ]
        },
        manicureSalonTableHeaders(): TableHeader[] {
            return [
                { text: 'Название салона', value: 'hrProjectName', width: '100px', align: 'left' },
                {
                    text: 'Имя сотрудника',
                    value: 'hrStaffName',
                    width: '100px',
                    align: 'left'
                },
                {
                    text: 'Доля указаных услуг по категориям по количеству маникюр и покрытие',
                    value: 'hrManicCoverCntShare',
                    align: 'right',
                },
                {
                    text: 'Доля указаных услуг по категориям по количеству моделирование и коррекция',
                    value: 'hrModelCorCntShare',
                    align: 'right',
                },
                {
                    text: 'Доля указаных услуг по категориям по количеству педикюр и покрытие',
                    value: 'hrPedicCoverCntShare',
                    align: 'right',
                },
                {
                    text: 'Доля указаных услуг по категориям по количеству уход',
                    value: 'hrCareCntShare',
                    align: 'right',
                },
                {
                    text: 'Доля указаных услуг по категориям по количеству смарт-педикюр',
                    value: 'hrSmartpedCntShare',
                    align: 'right',
                },
                {
                    text: 'Доля указаных услуг по категориям по количеству прочее',
                    value: 'hrOthersCntShare',
                    align: 'right',
                },
                {
                    text: 'Доля указаных услуг по категориям по количеству доля уходов от маникюров, педикюров и покрытий - не менее 10%',
                    value: 'hrCareManicPedicCoverCntShare',
                    align: 'right',
                },
                {
                    text: 'Доля указаных услуг по категориям по количеству доля смарт-педикюров от педикюров и покрытий - не менее 50%',
                    value: 'hrSmartpedPedicCoverCntShare',
                    align: 'right',
                },
                {
                    text: 'Среняя оценка по категориям оказаных услуг маникюр и покрытие',
                    value: 'hrManicCoverScore',
                    align: 'right',
                },
                {
                    text: 'Среняя оценка по категориям оказаных услуг моделирование и коррекция',
                    value: 'hrModelCorScore',
                    align: 'right',
                },
                {
                    text: 'Среняя оценка по категориям оказаных услуг педикюр и покрытие',
                    value: 'hrPedicCoverScore',
                    align: 'right',
                },
                {
                    text: 'Среняя оценка по категориям оказаных услуг уход',
                    value: 'hrCareScore',
                    align: 'right',
                },
                {
                    text: 'Среняя оценка по категориям оказаных услуг смарт-педикюр',
                    value: 'hrSmartpedScore',
                    align: 'right',
                },
                {
                    text: 'Среняя оценка по категориям оказаных услуг прочее',
                    value: 'hrOthersScore',
                    align: 'right',
                },
            ]
        },
        nailSalonTableHeaders4H(): TableHeader[] {
            return [
                {
                    text: 'Имя сотрудника',
                    value: 'staff_name',
                    width: '100px',
                    align: 'left'
                },
                {
                    text: 'Маникюр',
                    value: 'manicure_cnt',
                    align: 'right',
                },
                {
                    text: 'Маникюр',
                    value: 'manicure_cnt_share',
                    align: 'right',
                },
                {
                    text: 'Педикюр',
                    value: 'pedicure_cnt',
                    align: 'right',
                },
                {
                    text: 'Педикюр',
                    value: 'pedicure_cnt_share',
                    align: 'right',
                },
                {
                    text: 'Наращивание',
                    value: 'nail_extension_cnt',
                    align: 'right',
                },
                {
                    text: 'Наращивание',
                    value: 'nail_extension_cnt_share',
                    align: 'right',
                },
                {
                    text: 'Маникюр/педикюр мужской',
                    value: 'male_manicure_pedicure_cnt',
                    align: 'right',
                },
                {
                    text: 'Маникюр/педикюр мужской',
                    value: 'male_manicure_pedicure_cnt_share',
                    align: 'right',
                },
                {
                    text: 'Маникюр/педикюр детский',
                    value: 'kids_manicure_pedicure_cnt',
                    align: 'right',
                },
                {
                    text: 'Маникюр/педикюр детский',
                    value: 'kids_manicure_pedicure_cnt_share',
                    align: 'right',
                },
                {
                    text: 'Маникюр японский',
                    value: 'japanese_manicure_cnt',
                    align: 'right',
                },
                {
                    text: 'Маникюр японский',
                    value: 'japanese_manicure_cnt_share',
                    align: 'right',
                },
                {
                    text: 'Покрытие гель-лак',
                    value: 'gel_polish_cnt',
                    align: 'right',
                },
                {
                    text: 'Покрытие гель-лак',
                    value: 'gel_polish_cnt_share',
                    align: 'right',
                },
                {
                    text: 'Покрытие (все кроме гель-лака)',
                    value: 'coating_cnt',
                    align: 'right',
                },
                {
                    text: 'Покрытие (все кроме гель-лака)',
                    value: 'coating_cnt_share',
                    align: 'right',
                },
                {
                    text: 'Дизайн',
                    value: 'nail_design_cnt',
                    align: 'right',
                },
                {
                    text: 'Дизайн',
                    value: 'nail_design_cnt_share',
                    align: 'right',
                },
                {
                    text: 'Коррекция',
                    value: 'correction_cnt',
                    align: 'right',
                },
                {
                    text: 'Коррекция',
                    value: 'correction_cnt_share',
                    align: 'right',
                },
                {
                    text: 'Комплексы',
                    value: 'complex_nail_cnt',
                    align: 'right',
                },
                {
                    text: 'Комплексы',
                    value: 'complex_nail_cnt_share',
                    align: 'right',
                },
                {
                    text: 'Смарт педикюр',
                    value: 'smart_pedicure_cnt',
                    align: 'right',
                },
                {
                    text: 'Смарт педикюр',
                    value: 'smart_pedicure_cnt_share',
                    align: 'right',
                },
                {
                    text: 'Снятие гель-лака',
                    value: 'gel_polish_removal_cnt',
                    align: 'right',
                },
                {
                    text: 'Снятие гель-лака',
                    value: 'gel_polish_removal_cnt_share',
                    align: 'right',
                },
                {
                    text: 'Бразильский маникюр/педикюр/покрытие',
                    value: 'brazilian_nail_cnt',
                    align: 'right',
                },
                {
                    text: 'Бразильский маникюр/педикюр/покрытие',
                    value: 'brazilian_nail_cnt_share',
                    align: 'right',
                },
                {
                    text: 'Прочее',
                    value: 'other_nail_cnt',
                    align: 'right',
                },
                {
                    text: 'Прочее',
                    value: 'other_nail_cnt_share',
                    align: 'right',
                },
                {
                    text: 'Маникюр',
                    value: 'manicure_sum',
                    align: 'right',
                },
                {
                    text: 'Маникюр',
                    value: 'manicure_sum_share',
                    align: 'right',
                },
                {
                    text: 'Педикюр',
                    value: 'pedicure_sum',
                    align: 'right',
                },
                {
                    text: 'Педикюр',
                    value: 'pedicure_sum_share',
                    align: 'right',
                },
                {
                    text: 'Наращивание',
                    value: 'nail_extension_sum',
                    align: 'right',
                },
                {
                    text: 'Наращивание',
                    value: 'nail_extension_sum_share',
                    align: 'right',
                },
                {
                    text: 'Маникюр/педикюр мужской',
                    value: 'male_manicure_pedicure_sum',
                    align: 'right',
                },
                {
                    text: 'Маникюр/педикюр мужской',
                    value: 'male_manicure_pedicure_sum_share',
                    align: 'right',
                },
                {
                    text: 'Маникюр/педикюр детский',
                    value: 'kids_manicure_pedicure_sum',
                    align: 'right',
                },
                {
                    text: 'Маникюр/педикюр детский',
                    value: 'kids_manicure_pedicure_sum_share',
                    align: 'right',
                },
                {
                    text: 'Маникюр японский',
                    value: 'japanese_manicure_sum',
                    align: 'right',
                },
                {
                    text: 'Маникюр японский',
                    value: 'japanese_manicure_sum_share',
                    align: 'right',
                },
                {
                    text: 'Покрытие гель-лак',
                    value: 'gel_polish_sum',
                    align: 'right',
                },
                {
                    text: 'Покрытие гель-лак',
                    value: 'gel_polish_sum_share',
                    align: 'right',
                },
                {
                    text: 'Покрытие (все кроме гель-лака)',
                    value: 'coating_sum',
                    align: 'right',
                },
                {
                    text: 'Покрытие (все кроме гель-лака)',
                    value: 'coating_sum_share',
                    align: 'right',
                },
                {
                    text: 'Дизайн',
                    value: 'nail_design_sum',
                    align: 'right',
                },
                {
                    text: 'Дизайн',
                    value: 'nail_design_sum_share',
                    align: 'right',
                },
                {
                    text: 'Коррекция',
                    value: 'correction_sum',
                    align: 'right',
                },
                {
                    text: 'Коррекция',
                    value: 'correction_sum_share',
                    align: 'right',
                },
                {
                    text: 'Комплексы',
                    value: 'complex_nail_sum',
                    align: 'right',
                },
                {
                    text: 'Комплексы',
                    value: 'complex_nail_sum_share',
                    align: 'right',
                },
                {
                    text: 'Смарт педикюр',
                    value: 'smart_pedicure_sum',
                    align: 'right',
                },
                {
                    text: 'Смарт педикюр',
                    value: 'smart_pedicure_sum_share',
                    align: 'right',
                },
                {
                    text: 'Снятие гель-лака',
                    value: 'gel_polish_removal_sum',
                    align: 'right',
                },
                {
                    text: 'Снятие гель-лака',
                    value: 'gel_polish_removal_sum_share',
                    align: 'right',
                },
                {
                    text: 'Бразильский маникюр/педикюр/покрытие',
                    value: 'brazilian_nail_sum',
                    align: 'right',
                },
                {
                    text: 'Бразильский маникюр/педикюр/покрытие',
                    value: 'brazilian_nail_sum_share',
                    align: 'right',
                },
                {
                    text: 'Прочее',
                    value: 'other_nail_sum',
                    align: 'right',
                },
                {
                    text: 'Прочее',
                    value: 'other_nail_sum_share',
                    align: 'right',
                },
                {
                    text: 'Маникюр',
                    value: 'manicure_score',
                    align: 'right',
                },
                {
                    text: 'Педикюр',
                    value: 'pedicure_score',
                    align: 'right',
                },
                {
                    text: 'Наращивание',
                    value: 'nail_extension_score',
                    align: 'right',
                },
                {
                    text: 'Маникюр/педикюр мужской',
                    value: 'male_manicure_pedicure_score',
                    align: 'right',
                },
                {
                    text: 'Маникюр/педикюр детский',
                    value: 'kids_manicure_pedicure_score',
                    align: 'right',
                },
                {
                    text: 'Маникюр японский',
                    value: 'japanese_manicure_score',
                    align: 'right',
                },
                {
                    text: 'Покрытие гель-лак',
                    value: 'gel_polish_score',
                    align: 'right',
                },
                {
                    text: 'Покрытие (все кроме гель-лака)',
                    value: 'coating_score',
                    align: 'right',
                },
                {
                    text: 'Дизайн',
                    value: 'nail_design_score',
                    align: 'right',
                },
                {
                    text: 'Коррекция',
                    value: 'correction_score',
                    align: 'right',
                },
                {
                    text: 'Комплексы',
                    value: 'complex_nail_score',
                    align: 'right',
                },
                {
                    text: 'Смарт педикюр',
                    value: 'smart_pedicure_score',
                    align: 'right',
                },
                {
                    text: 'Снятие гель-лака',
                    value: 'gel_polish_removal_score',
                    align: 'right',
                },
                {
                    text: 'Бразильский маникюр/педикюр/покрытие',
                    value: 'brazilian_nail_score',
                    align: 'right',
                },
                {
                    text: 'Прочее',
                    value: 'other_nail_score',
                    align: 'right',
                },
                {
                    text: 'Маникюр',
                    value: 'manicure_first_rec',
                    align: 'right',
                },
                {
                    text: 'Педикюр',
                    value: 'pedicure_first_rec',
                    align: 'right',
                },
                {
                    text: 'Наращивание',
                    value: 'nail_extension_first_rec',
                    align: 'right',
                },
                {
                    text: 'Маникюр/педикюр мужской',
                    value: 'male_manicure_pedicure_first_rec',
                    align: 'right',
                },
                {
                    text: 'Маникюр/педикюр детский',
                    value: 'kids_manicure_pedicure_first_rec',
                    align: 'right',
                },
                {
                    text: 'Маникюр японский',
                    value: 'japanese_manicure_first_rec',
                    align: 'right',
                },
                {
                    text: 'Покрытие гель-лак',
                    value: 'gel_polish_first_rec',
                    align: 'right',
                },
                {
                    text: 'Покрытие (все кроме гель-лака)',
                    value: 'coating_first_rec',
                    align: 'right',
                },
                {
                    text: 'Дизайн',
                    value: 'nail_design_first_rec',
                    align: 'right',
                },
                {
                    text: 'Коррекция',
                    value: 'correction_first_rec',
                    align: 'right',
                },
                {
                    text: 'Комплексы',
                    value: 'complex_nail_first_rec',
                    align: 'right',
                },
                {
                    text: 'Смарт педикюр',
                    value: 'smart_pedicure_first_rec',
                    align: 'right',
                },
                {
                    text: 'Снятие гель-лака',
                    value: 'gel_polish_removal_first_rec',
                    align: 'right',
                },
                {
                    text: 'Бразильский маникюр/педикюр/покрытие',
                    value: 'brazilian_nail_first_rec',
                    align: 'right',
                },
                {
                    text: 'Прочее',
                    value: 'other_nail_first_rec',
                    align: 'right',
                },
            ]
        },
        nail4HColumns() {
            return [
                {
                    key: 'manicure',
                    label: 'Маникюр',
                    cnt: 'manicure_cnt',
                    cnt_share: 'manicure_cnt_share',
                    sum: 'manicure_sum',
                    sum_share: 'manicure_sum_share',
                    score: 'manicure_score',
                    first_rec: 'manicure_first_rec',
                },
                {
                    key: 'pedicure',
                    label: 'Педикюр',
                    cnt: 'pedicure_cnt',
                    cnt_share: 'pedicure_cnt_share',
                    sum: 'pedicure_sum',
                    sum_share: 'pedicure_sum_share',
                    score: 'pedicure_score',
                    first_rec: 'pedicure_first_rec',
                },
                {
                    key: 'nail_extension',
                    label: 'Наращивание',
                    cnt: 'nail_extension_cnt',
                    cnt_share: 'nail_extension_cnt_share',
                    sum: 'nail_extension_sum',
                    sum_share: 'nail_extension_sum_share',
                    score: 'nail_extension_score',
                    first_rec: 'nail_extension_first_rec',
                },
                {
                    key: 'male_manicure_pedicure',
                    label: 'Маникюр/педикюр мужской',
                    cnt: 'male_manicure_pedicure_cnt',
                    cnt_share: 'male_manicure_pedicure_cnt_share',
                    sum: 'male_manicure_pedicure_sum',
                    sum_share: 'male_manicure_pedicure_sum_share',
                    score: 'male_manicure_pedicure_score',
                    first_rec: 'male_manicure_pedicure_first_rec',
                },
                {
                    key: 'kids_manicure_pedicure',
                    label: 'Маникюр/педикюр детский',
                    cnt: 'kids_manicure_pedicure_cnt',
                    cnt_share: 'kids_manicure_pedicure_cnt_share',
                    sum: 'kids_manicure_pedicure_sum',
                    sum_share: 'kids_manicure_pedicure_sum_share',
                    score: 'kids_manicure_pedicure_score',
                    first_rec: 'kids_manicure_pedicure_first_rec',
                },
                {
                    key: 'japanese_manicure',
                    label: 'Маникюр японский',
                    cnt: 'japanese_manicure_cnt',
                    cnt_share: 'japanese_manicure_cnt_share',
                    sum: 'japanese_manicure_sum',
                    sum_share: 'japanese_manicure_sum_share',
                    score: 'japanese_manicure_score',
                    first_rec: 'japanese_manicure_first_rec',
                },
                {
                    key: 'gel_polish',
                    label: 'Покрытие гель-лак',
                    cnt: 'gel_polish_cnt',
                    cnt_share: 'gel_polish_cnt_share',
                    sum: 'gel_polish_sum',
                    sum_share: 'gel_polish_sum_share',
                    score: 'gel_polish_score',
                    first_rec: 'gel_polish_first_rec',
                },
                {
                    key: 'coating',
                    label: 'Покрытие (все кроме гель-лака)',
                    cnt: 'coating_cnt',
                    cnt_share: 'coating_cnt_share',
                    sum: 'coating_sum',
                    sum_share: 'coating_sum_share',
                    score: 'coating_score',
                    first_rec: 'coating_first_rec',
                },
                {
                    key: 'nail_design',
                    label: 'Дизайн',
                    cnt: 'nail_design_cnt',
                    cnt_share: 'nail_design_cnt_share',
                    sum: 'nail_design_sum',
                    sum_share: 'nail_design_sum_share',
                    score: 'nail_design_score',
                    first_rec: 'nail_design_first_rec',
                },
                {
                    key: 'correction',
                    label: 'Коррекция',
                    cnt: 'correction_cnt',
                    cnt_share: 'correction_cnt_share',
                    sum: 'correction_sum',
                    sum_share: 'correction_sum_share',
                    score: 'correction_score',
                    first_rec: 'correction_first_rec',
                },
                {
                    key: 'complex_nail',
                    label: 'Комплексы',
                    cnt: 'complex_nail_cnt',
                    cnt_share: 'complex_nail_cnt_share',
                    sum: 'complex_nail_sum',
                    sum_share: 'complex_nail_sum_share',
                    score: 'complex_nail_score',
                    first_rec: 'complex_nail_first_rec',
                },
                {
                    key: 'smart_pedicure',
                    label: 'Смарт педикюр',
                    cnt: 'smart_pedicure_cnt',
                    cnt_share: 'smart_pedicure_cnt_share',
                    sum: 'smart_pedicure_sum',
                    sum_share: 'smart_pedicure_sum_share',
                    score: 'smart_pedicure_score',
                    first_rec: 'smart_pedicure_first_rec',
                },
                {
                    key: 'gel_polish_removal',
                    label: 'Снятие гель-лака',
                    cnt: 'gel_polish_removal_cnt',
                    cnt_share: 'gel_polish_removal_cnt_share',
                    sum: 'gel_polish_removal_sum',
                    sum_share: 'gel_polish_removal_sum_share',
                    score: 'gel_polish_removal_score',
                    first_rec: 'gel_polish_removal_first_rec',
                },
                {
                    key: 'brazilian_nail',
                    label: 'Бразильский маникюр/педикюр/покрытие',
                    cnt: 'brazilian_nail_cnt',
                    cnt_share: 'brazilian_nail_cnt_share',
                    sum: 'brazilian_nail_sum',
                    sum_share: 'brazilian_nail_sum_share',
                    score: 'brazilian_nail_score',
                    first_rec: 'brazilian_nail_first_rec',
                },
                {
                    key: 'other_nail',
                    label: 'Прочее',
                    cnt: 'other_nail_cnt',
                    cnt_share: 'other_nail_cnt_share',
                    sum: 'other_nail_sum',
                    sum_share: 'other_nail_sum_share',
                    score: 'other_nail_score',
                    first_rec: 'other_nail_first_rec',
                },
            ]
        },
        hairdresserMakeupStylist4HColumns() {
            return [
                { key: 'female_haircut', label: 'Стрижка женская', cnt: 'female_haircut_cnt', cnt_share: 'female_haircut_cnt_share', sum: 'female_haircut_sum', sum_share: 'female_haircut_sum_share', score: 'female_haircut_score', first_rec: 'female_haircut_first_rec' },
                { key: 'male_kids_haircut', label: 'Стрижка мужская + детская', cnt: 'male_kids_haircut_cnt', cnt_share: 'male_kids_haircut_cnt_share', sum: 'male_kids_haircut_sum', sum_share: 'male_kids_haircut_sum_share', score: 'male_kids_haircut_score', first_rec: 'male_kids_haircut_first_rec' },
                { key: 'coloring', label: 'Окрашивание (общее)', cnt: 'coloring_cnt', cnt_share: 'coloring_cnt_share', sum: 'coloring_sum', sum_share: 'coloring_sum_share', score: 'coloring_score', first_rec: 'coloring_first_rec' },
                { key: 'highlight_tone', label: 'Мелирование/тонирование', cnt: 'highlight_tone_cnt', cnt_share: 'highlight_tone_cnt_share', sum: 'highlight_tone_sum', sum_share: 'highlight_tone_sum_share', score: 'highlight_tone_score', first_rec: 'highlight_tone_first_rec' },
                { key: 'treatments', label: 'Уход', cnt: 'treatments_cnt', cnt_share: 'treatments_cnt_share', sum: 'treatments_sum', sum_share: 'treatments_sum_share', score: 'treatments_score', first_rec: 'treatments_first_rec' },
                { key: 'makeup', label: 'Макияж (общий)', cnt: 'makeup_cnt', cnt_share: 'makeup_cnt_share', sum: 'makeup_sum', sum_share: 'makeup_sum_share', score: 'makeup_score', first_rec: 'makeup_first_rec' },
                { key: 'day_makeup', label: 'Макияж дневной', cnt: 'day_makeup_cnt', cnt_share: 'day_makeup_cnt_share', sum: 'day_makeup_sum', sum_share: 'day_makeup_sum_share', score: 'day_makeup_score', first_rec: 'day_makeup_first_rec' },
                { key: 'evening_makeup', label: 'Макияж вечерний', cnt: 'evening_makeup_cnt', cnt_share: 'evening_makeup_cnt_share', sum: 'evening_makeup_sum', sum_share: 'evening_makeup_sum_share', score: 'evening_makeup_score', first_rec: 'evening_makeup_first_rec' },
                { key: 'wedding_makeup', label: 'Макияж свадебный', cnt: 'wedding_makeup_cnt', cnt_share: 'wedding_makeup_cnt_share', sum: 'wedding_makeup_sum', sum_share: 'wedding_makeup_sum_share', score: 'wedding_makeup_score', first_rec: 'wedding_makeup_first_rec' },
                { key: 'express_makeup', label: 'Экспресс макияж', cnt: 'express_makeup_cnt', cnt_share: 'express_makeup_cnt_share', sum: 'express_makeup_sum', sum_share: 'express_makeup_sum_share', score: 'express_makeup_score', first_rec: 'express_makeup_first_rec' },
                { key: 'hairstyle', label: 'Прическа (общая)', cnt: 'hairstyle_cnt', cnt_share: 'hairstyle_cnt_share', sum: 'hairstyle_sum', sum_share: 'hairstyle_sum_share', score: 'hairstyle_score', first_rec: 'hairstyle_first_rec' },
                { key: 'styling', label: 'Укладка (общая + локоны)', cnt: 'styling_cnt', cnt_share: 'styling_cnt_share', sum: 'styling_sum', sum_share: 'styling_sum_share', score: 'styling_score', first_rec: 'styling_first_rec' },
                { key: 'complex_hair_makeup', label: 'Комплексы', cnt: 'complex_hair_makeup_cnt', cnt_share: 'complex_hair_makeup_cnt_share', sum: 'complex_hair_makeup_sum', sum_share: 'complex_hair_makeup_sum_share', score: 'complex_hair_makeup_score', first_rec: 'complex_hair_makeup_first_rec' },
            ]
        },
        lashBrow4HColumns() {
            return [
                { key: 'lash_lamination', label: 'Ламинирование ресниц', cnt: 'lash_lamination_cnt', cnt_share: 'lash_lamination_cnt_share', sum: 'lash_lamination_sum', sum_share: 'lash_lamination_sum_share', score: 'lash_lamination_score', first_rec: 'lash_lamination_first_rec' },
                { key: 'lash_extension', label: 'Наращивание ресниц', cnt: 'lash_extension_cnt', cnt_share: 'lash_extension_cnt_share', sum: 'lash_extension_sum', sum_share: 'lash_extension_sum_share', score: 'lash_extension_score', first_rec: 'lash_extension_first_rec' },
                { key: 'express_lash_extension', label: 'Эксперсс наращивание', cnt: 'express_lash_extension_cnt', cnt_share: 'express_lash_extension_cnt_share', sum: 'express_lash_extension_sum', sum_share: 'express_lash_extension_sum_share', score: 'express_lash_extension_score', first_rec: 'express_lash_extension_first_rec' },
                { key: 'lower_lash_extension', label: 'Нижнее наращивание', cnt: 'lower_lash_extension_cnt', cnt_share: 'lower_lash_extension_cnt_share', sum: 'lower_lash_extension_sum', sum_share: 'lower_lash_extension_sum_share', score: 'lower_lash_extension_score', first_rec: 'lower_lash_extension_first_rec' },
                { key: 'removal_extended_lash', label: 'Снятие наращенных ресниц', cnt: 'removal_extended_lash_cnt', cnt_share: 'removal_extended_lash_cnt_share', sum: 'removal_extended_lash_sum', sum_share: 'removal_extended_lash_sum_share', score: 'removal_extended_lash_score', first_rec: 'removal_extended_lash_first_rec' },
                { key: 'hollywood_volume', label: 'Голливудский объем', cnt: 'hollywood_volume_cnt', cnt_share: 'hollywood_volume_cnt_share', sum: 'hollywood_volume_sum', sum_share: 'hollywood_volume_sum_share', score: 'hollywood_volume_score', first_rec: 'hollywood_volume_first_rec' },
                { key: 'extra_lashes', label: 'Дополнительно: эффект мокрых ресниц, лучи, цветные ресницы и прочее', cnt: 'extra_lashes_cnt', cnt_share: 'extra_lashes_cnt_share', sum: 'extra_lashes_sum', sum_share: 'extra_lashes_sum_share', score: 'extra_lashes_score', first_rec: 'extra_lashes_first_rec' },
                { key: 'lash_correction', label: 'Коррекция ресниц', cnt: 'lash_correction_cnt', cnt_share: 'lash_correction_cnt_share', sum: 'lash_correction_sum', sum_share: 'lash_correction_sum_share', score: 'lash_correction_score', first_rec: 'lash_correction_first_rec' },
                { key: 'brow_lamination', label: 'Ламинирование бровей', cnt: 'brow_lamination_cnt', cnt_share: 'brow_lamination_cnt_share', sum: 'brow_lamination_sum', sum_share: 'brow_lamination_sum_share', score: 'brow_lamination_score', first_rec: 'brow_lamination_first_rec' },
                { key: 'brow_correction', label: 'Коррекция бровей', cnt: 'brow_correction_cnt', cnt_share: 'brow_correction_cnt_share', sum: 'brow_correction_sum', sum_share: 'brow_correction_sum_share', score: 'brow_correction_score', first_rec: 'brow_correction_first_rec' },
                { key: 'brow_coloring', label: 'Окрашивание бровей', cnt: 'brow_coloring_cnt', cnt_share: 'brow_coloring_cnt_share', sum: 'brow_coloring_sum', sum_share: 'brow_coloring_sum_share', score: 'brow_coloring_score', first_rec: 'brow_coloring_first_rec' },
                { key: 'brow_spa', label: 'Спа для бровей', cnt: 'brow_spa_cnt', cnt_share: 'brow_spa_cnt_share', sum: 'brow_spa_sum', sum_share: 'brow_spa_sum_share', score: 'brow_spa_score', first_rec: 'brow_spa_first_rec' },
                { key: 'complex_lash_brow', label: 'Комплексы', cnt: 'complex_lash_brow_cnt', cnt_share: 'complex_lash_brow_cnt_share', sum: 'complex_lash_brow_sum', sum_share: 'complex_lash_brow_sum_share', score: 'complex_lash_brow_score', first_rec: 'complex_lash_brow_first_rec' },
                { key: 'other_lash_brow', label: 'Прочее', cnt: 'other_lash_brow_cnt', cnt_share: 'other_lash_brow_cnt_share', sum: 'other_lash_brow_sum', sum_share: 'other_lash_brow_sum_share', score: 'other_lash_brow_score', first_rec: 'other_lash_brow_first_rec' },
            ]
        },
        massage4HColumns() {
            return [
                { key: 'classic_massage', label: 'Массаж классический (общий)', cnt: 'classic_massage_cnt', cnt_share: 'classic_massage_cnt_share', sum: 'classic_massage_sum', sum_share: 'classic_massage_sum_share', score: 'classic_massage_score', first_rec: 'classic_massage_first_rec' },
                { key: 'lymphatic_massage', label: 'Массаж лимфодренажный', cnt: 'lymphatic_massage_cnt', cnt_share: 'lymphatic_massage_cnt_share', sum: 'lymphatic_massage_sum', sum_share: 'lymphatic_massage_sum_share', score: 'lymphatic_massage_score', first_rec: 'lymphatic_massage_first_rec' },
                { key: 'anticellulite_massage', label: 'Массаж антицеллюлитный', cnt: 'anticellulite_massage_cnt', cnt_share: 'anticellulite_massage_cnt_share', sum: 'anticellulite_massage_sum', sum_share: 'anticellulite_massage_sum_share', score: 'anticellulite_massage_score', first_rec: 'anticellulite_massage_first_rec' },
                { key: 'legs_massage', label: 'Массаж стоп и голеней (+ноги)', cnt: 'legs_massage_cnt', cnt_share: 'legs_massage_cnt_share', sum: 'legs_massage_sum', sum_share: 'legs_massage_sum_share', score: 'legs_massage_score', first_rec: 'legs_massage_first_rec' },
                { key: 'back_massage', label: 'Массаж спины', cnt: 'back_massage_cnt', cnt_share: 'back_massage_cnt_share', sum: 'back_massage_sum', sum_share: 'back_massage_sum_share', score: 'back_massage_score', first_rec: 'back_massage_first_rec' },
                { key: 'neck_massage', label: 'Массаж шейно-воротниковой зоны', cnt: 'neck_massage_cnt', cnt_share: 'neck_massage_cnt_share', sum: 'neck_massage_sum', sum_share: 'neck_massage_sum_share', score: 'neck_massage_score', first_rec: 'neck_massage_first_rec' },
                { key: 'face_massage', label: 'Массаж лица', cnt: 'face_massage_cnt', cnt_share: 'face_massage_cnt_share', sum: 'face_massage_sum', sum_share: 'face_massage_sum_share', score: 'face_massage_score', first_rec: 'face_massage_first_rec' },
                { key: 'energy_massage', label: 'Массаж энергетический', cnt: 'energy_massage_cnt', cnt_share: 'energy_massage_cnt_share', sum: 'energy_massage_sum', sum_share: 'energy_massage_sum_share', score: 'energy_massage_score', first_rec: 'energy_massage_first_rec' },
                { key: 'top_master_massage', label: 'Массаж топ-мастер', cnt: 'top_master_massage_cnt', cnt_share: 'top_master_massage_cnt_share', sum: 'top_master_massage_sum', sum_share: 'top_master_massage_sum_share', score: 'top_master_massage_score', first_rec: 'top_master_massage_first_rec' },
                { key: 'other_massage', label: 'Прочее(массаж)', cnt: 'other_massage_cnt', cnt_share: 'other_massage_cnt_share', sum: 'other_massage_sum', sum_share: 'other_massage_sum_share', score: 'other_massage_score', first_rec: 'other_massage_first_rec' },
            ]
        },
        epilation4HColumns() {
            return [
                { key: 'armpits', label: 'Подмышечные впадины', cnt: 'armpits_cnt', cnt_share: 'armpits_cnt_share', sum: 'armpits_sum', sum_share: 'armpits_sum_share', score: 'armpits_score', first_rec: 'armpits_first_rec' },
                { key: 'bikini', label: 'Бикини', cnt: 'bikini_cnt', cnt_share: 'bikini_cnt_share', sum: 'bikini_sum', sum_share: 'bikini_sum_share', score: 'bikini_score', first_rec: 'bikini_first_rec' },
                { key: 'deep_bikini', label: 'Глубокое бикини', cnt: 'deep_bikini_cnt', cnt_share: 'deep_bikini_cnt_share', sum: 'deep_bikini_sum', sum_share: 'deep_bikini_sum_share', score: 'deep_bikini_score', first_rec: 'deep_bikini_first_rec' },
                { key: 'legs', label: 'Ноги', cnt: 'legs_cnt', cnt_share: 'legs_cnt_share', sum: 'legs_sum', sum_share: 'legs_sum_share', score: 'legs_score', first_rec: 'legs_first_rec' },
                { key: 'stomach', label: 'Живот', cnt: 'stomach_cnt', cnt_share: 'stomach_cnt_share', sum: 'stomach_sum', sum_share: 'stomach_sum_share', score: 'stomach_score', first_rec: 'stomach_first_rec' },
                { key: 'arms', label: 'Руки', cnt: 'arms_cnt', cnt_share: 'arms_cnt_share', sum: 'arms_sum', sum_share: 'arms_sum_share', score: 'arms_score', first_rec: 'arms_first_rec' },
                { key: 'anesthetic', label: 'Обезболивающее средство', cnt: 'anesthetic_cnt', cnt_share: 'anesthetic_cnt_share', sum: 'anesthetic_sum', sum_share: 'anesthetic_sum_share', score: 'anesthetic_score', first_rec: 'anesthetic_first_rec' },
                { key: 'male_epilation', label: 'Мужская эпиляция', cnt: 'male_epilation_cnt', cnt_share: 'male_epilation_cnt_share', sum: 'male_epilation_sum', sum_share: 'male_epilation_sum_share', score: 'male_epilation_score', first_rec: 'male_epilation_first_rec' },
                { key: 'other_epilation', label: 'Прочее (эпиляция)', cnt: 'other_epilation_cnt', cnt_share: 'other_epilation_cnt_share', sum: 'other_epilation_sum', sum_share: 'other_epilation_sum_share', score: 'other_epilation_score', first_rec: 'other_epilation_first_rec' },
            ]
        },
        lashBrowSalonTableHeaders4H(): TableHeader[] {
            return [
                {
                    text: 'Имя сотрудника',
                    value: 'staff_name',
                    width: '100px',
                    align: 'left'
                },
                {
                    text: 'Ламинирование ресниц',
                    value: 'lash_lamination_cnt',
                    align: 'right',
                },
                {
                    text: 'Ламинирование ресниц',
                    value: 'lash_lamination_cnt_share',
                    align: 'right',
                },
                {
                    text: 'Наращивание ресниц',
                    value: 'lash_extension_cnt',
                    align: 'right',
                },
                {
                    text: 'Наращивание ресниц',
                    value: 'lash_extension_cnt_share',
                    align: 'right',
                },
                {
                    text: 'Эксперсс наращивание',
                    value: 'express_lash_extension_cnt',
                    align: 'right',
                },
                {
                    text: 'Эксперсс наращивание',
                    value: 'express_lash_extension_cnt_share',
                    align: 'right',
                },
                {
                    text: 'Нижнее наращивание',
                    value: 'lower_lash_extension_cnt',
                    align: 'right',
                },
                {
                    text: 'Нижнее наращивание',
                    value: 'lower_lash_extension_cnt_share',
                    align: 'right',
                },
                {
                    text: 'Снятие наращенных ресниц',
                    value: 'removal_extended_lash_cnt',
                    align: 'right',
                },
                {
                    text: 'Снятие наращенных ресниц',
                    value: 'removal_extended_lash_cnt_share',
                    align: 'right',
                },
                {
                    text: 'Голливудский объем',
                    value: 'hollywood_volume_cnt',
                    align: 'right',
                },
                {
                    text: 'Голливудский объем',
                    value: 'hollywood_volume_cnt_share',
                    align: 'right',
                },
                {
                    text: 'Дополнительно: эффект мокрых ресниц, лучи, цветные ресницы и прочее',
                    value: 'extra_lashes_cnt',
                    align: 'right',
                },
                {
                    text: 'Дополнительно: эффект мокрых ресниц, лучи, цветные ресницы и прочее',
                    value: 'extra_lashes_cnt_share',
                    align: 'right',
                },
                {
                    text: 'Ламинирование бровей',
                    value: 'brow_lamination_cnt',
                    align: 'right',
                },
                {
                    text: 'Ламинирование бровей',
                    value: 'brow_lamination_cnt_share',
                    align: 'right',
                },
                {
                    text: 'Окрашивание бровей',
                    value: 'brow_coloring_cnt',
                    align: 'right',
                },
                {
                    text: 'Окрашивание бровей',
                    value: 'brow_coloring_cnt_share',
                    align: 'right',
                },
                {
                    text: 'Окрашиваине бровей хной',
                    value: 'brow_henna_coloring_cnt',
                    align: 'right',
                },
                {
                    text: 'Окрашиваине бровей хной',
                    value: 'brow_henna_coloring_cnt_share',
                    align: 'right',
                },
                {
                    text: 'Коррекция формы бровей',
                    value: 'brow_correction_cnt',
                    align: 'right',
                },
                {
                    text: 'Коррекция формы бровей',
                    value: 'brow_correction_cnt_share',
                    align: 'right',
                },
                {
                    text: 'Спа для бровей',
                    value: 'brow_spa_cnt',
                    align: 'right',
                },
                {
                    text: 'Спа для бровей',
                    value: 'brow_spa_cnt_share',
                    align: 'right',
                },
                {
                    text: 'Комплексы',
                    value: 'complex_lash_brow_cnt',
                    align: 'right',
                },
                {
                    text: 'Комплексы',
                    value: 'complex_lash_brow_cnt_share',
                    align: 'right',
                },
                {
                    text: 'Прочее',
                    value: 'other_lash_brow_cnt',
                    align: 'right',
                },
                {
                    text: 'Прочее',
                    value: 'other_lash_brow_cnt_share',
                    align: 'right',
                },
                {
                    text: 'Ламинирование ресниц',
                    value: 'lash_lamination_sum',
                    align: 'right',
                },
                {
                    text: 'Ламинирование ресниц',
                    value: 'lash_lamination_sum_share',
                    align: 'right',
                },
                {
                    text: 'Наращивание ресниц',
                    value: 'lash_extension_sum',
                    align: 'right',
                },
                {
                    text: 'Наращивание ресниц',
                    value: 'lash_extension_sum_share',
                    align: 'right',
                },
                {
                    text: 'Эксперсс наращивание',
                    value: 'express_lash_extension_sum',
                    align: 'right',
                },
                {
                    text: 'Эксперсс наращивание',
                    value: 'express_lash_extension_sum_share',
                    align: 'right',
                },
                {
                    text: 'Нижнее наращивание',
                    value: 'lower_lash_extension_sum',
                    align: 'right',
                },
                {
                    text: 'Нижнее наращивание',
                    value: 'lower_lash_extension_sum_share',
                    align: 'right',
                },
                {
                    text: 'Снятие наращенных ресниц',
                    value: 'removal_extended_lash_sum',
                    align: 'right',
                },
                {
                    text: 'Снятие наращенных ресниц',
                    value: 'removal_extended_lash_sum_share',
                    align: 'right',
                },
                {
                    text: 'Голливудский объем',
                    value: 'hollywood_volume_sum',
                    align: 'right',
                },
                {
                    text: 'Голливудский объем',
                    value: 'hollywood_volume_sum_share',
                    align: 'right',
                },
                {
                    text: 'Дополнительно: эффект мокрых ресниц, лучи, цветные ресницы и прочее',
                    value: 'extra_lashes_sum',
                    align: 'right',
                },
                {
                    text: 'Дополнительно: эффект мокрых ресниц, лучи, цветные ресницы и прочее',
                    value: 'extra_lashes_sum_share',
                    align: 'right',
                },
                {
                    text: 'Ламинирование бровей',
                    value: 'brow_lamination_sum',
                    align: 'right',
                },
                {
                    text: 'Ламинирование бровей',
                    value: 'brow_lamination_sum_share',
                    align: 'right',
                },
                {
                    text: 'Окрашивание бровей',
                    value: 'brow_coloring_sum',
                    align: 'right',
                },
                {
                    text: 'Окрашивание бровей',
                    value: 'brow_coloring_sum_share',
                    align: 'right',
                },
                {
                    text: 'Окрашиваине бровей хной',
                    value: 'brow_henna_coloring_sum',
                    align: 'right',
                },
                {
                    text: 'Окрашиваине бровей хной',
                    value: 'brow_henna_coloring_sum_share',
                    align: 'right',
                },
                {
                    text: 'Коррекция формы бровей',
                    value: 'brow_correction_sum',
                    align: 'right',
                },
                {
                    text: 'Коррекция формы бровей',
                    value: 'brow_correction_sum_share',
                    align: 'right',
                },
                {
                    text: 'Спа для бровей',
                    value: 'brow_spa_sum',
                    align: 'right',
                },
                {
                    text: 'Спа для бровей',
                    value: 'brow_spa_sum_share',
                    align: 'right',
                },
                {
                    text: 'Комплексы',
                    value: 'complex_lash_brow_sum',
                    align: 'right',
                },
                {
                    text: 'Комплексы',
                    value: 'complex_lash_brow_sum_share',
                    align: 'right',
                },
                {
                    text: 'Прочее',
                    value: 'other_lash_brow_sum',
                    align: 'right',
                },
                {
                    text: 'Прочее',
                    value: 'other_lash_brow_sum_share',
                    align: 'right',
                },
                {
                    text: 'Ламинирование ресниц',
                    value: 'lash_lamination_score',
                    align: 'right',
                },
                {
                    text: 'Наращивание ресниц',
                    value: 'lash_extension_score',
                    align: 'right',
                },
                {
                    text: 'Эксперсс наращивание',
                    value: 'express_lash_extension_score',
                    align: 'right',
                },
                {
                    text: 'Нижнее наращивание',
                    value: 'lower_lash_extension_score',
                    align: 'right',
                },
                {
                    text: 'Снятие наращенных ресниц',
                    value: 'removal_extended_lash_score',
                    align: 'right',
                },
                {
                    text: 'Голливудский объем',
                    value: 'hollywood_volume_score',
                    align: 'right',
                },
                {
                    text: 'Дополнительно: эффект мокрых ресниц, лучи, цветные ресницы и прочее',
                    value: 'extra_lashes_score',
                    align: 'right',
                },
                {
                    text: 'Ламинирование бровей',
                    value: 'brow_lamination_score',
                    align: 'right',
                },
                {
                    text: 'Окрашивание бровей',
                    value: 'brow_coloring_score',
                    align: 'right',
                },
                {
                    text: 'Окрашиваине бровей хной',
                    value: 'brow_henna_coloring_score',
                    align: 'right',
                },
                {
                    text: 'Коррекция формы бровей',
                    value: 'brow_correction_score',
                    align: 'right',
                },
                {
                    text: 'Спа для бровей',
                    value: 'brow_spa_score',
                    align: 'right',
                },
                {
                    text: 'Комплексы',
                    value: 'complex_lash_brow_score',
                    align: 'right',
                },
                {
                    text: 'Прочее',
                    value: 'other_lash_brow_score',
                    align: 'right',
                },
                {
                    text: 'Ламинирование ресниц',
                    value: 'lash_lamination_first_rec',
                    align: 'right',
                },
                {
                    text: 'Наращивание ресниц',
                    value: 'lash_extension_first_rec',
                    align: 'right',
                },
                {
                    text: 'Эксперсс наращивание',
                    value: 'express_lash_extension_first_rec',
                    align: 'right',
                },
                {
                    text: 'Нижнее наращивание',
                    value: 'lower_lash_extension_first_rec',
                    align: 'right',
                },
                {
                    text: 'Снятие наращенных ресниц',
                    value: 'removal_extended_lash_first_rec',
                    align: 'right',
                },
                {
                    text: 'Голливудский объем',
                    value: 'hollywood_volume_first_rec',
                    align: 'right',
                },
                {
                    text: 'Дополнительно: эффект мокрых ресниц, лучи, цветные ресницы и прочее',
                    value: 'extra_lashes_first_rec',
                    align: 'right',
                },
                {
                    text: 'Ламинирование бровей',
                    value: 'brow_lamination_first_rec',
                    align: 'right',
                },
                {
                    text: 'Окрашивание бровей',
                    value: 'brow_coloring_first_rec',
                    align: 'right',
                },
                {
                    text: 'Окрашиваине бровей хной',
                    value: 'brow_henna_coloring_first_rec',
                    align: 'right',
                },
                {
                    text: 'Коррекция формы бровей',
                    value: 'brow_correction_first_rec',
                    align: 'right',
                },
                {
                    text: 'Спа для бровей',
                    value: 'brow_spa_first_rec',
                    align: 'right',
                },
                {
                    text: 'Комплексы',
                    value: 'complex_lash_brow_first_rec',
                    align: 'right',
                },
                {
                    text: 'Прочее',
                    value: 'other_lash_brow_first_rec',
                    align: 'right',
                },
            ]
        },
        hairdresserMakeupStylistSalonTableHeaders4H(): TableHeader[] {
            return [
                {
                    text: 'Имя сотрудника',
                    value: 'staff_name',
                    width: '100px',
                    align: 'left'
                },
                {
                    text: 'Стрижка женская',
                    value: 'female_haircut_cnt',
                    align: 'right',
                },
                {
                    text: 'Стрижка женская',
                    value: 'female_haircut_cnt_share',
                    align: 'right',
                },
                {
                    text: 'Стрижка мужская + детская',
                    value: 'male_kids_haircut_cnt',
                    align: 'right',
                },
                {
                    text: 'Стрижка мужская + детская',
                    value: 'male_kids_haircut_cnt_share',
                    align: 'right',
                },
                {
                    text: 'Окрашивание (общее)',
                    value: 'coloring_cnt',
                    align: 'right',
                },
                {
                    text: 'Окрашивание (общее)',
                    value: 'coloring_cnt_share',
                    align: 'right',
                },
                {
                    text: 'Мелирование/тонирование',
                    value: 'highlight_tone_cnt',
                    align: 'right',
                },
                {
                    text: 'Мелирование/тонирование',
                    value: 'highlight_tone_cnt_share',
                    align: 'right',
                },
                {
                    text: 'Уход',
                    value: 'treatments_cnt',
                    align: 'right',
                },
                {
                    text: 'Уход',
                    value: 'treatments_cnt_share',
                    align: 'right',
                },
                {
                    text: 'Макияж (общий)',
                    value: 'makeup_cnt',
                    align: 'right',
                },
                {
                    text: 'Макияж (общий)',
                    value: 'makeup_cnt_share',
                    align: 'right',
                },
                {
                    text: 'Макияж дневной',
                    value: 'day_makeup_cnt',
                    align: 'right',
                },
                {
                    text: 'Макияж дневной',
                    value: 'day_makeup_cnt_share',
                    align: 'right',
                },
                {
                    text: 'Макияж вечерний',
                    value: 'evening_makeup_cnt',
                    align: 'right',
                },
                {
                    text: 'Макияж вечерний',
                    value: 'evening_makeup_cnt_share',
                    align: 'right',
                },
                {
                    text: 'Макияж свадебный',
                    value: 'wedding_makeup_cnt',
                    align: 'right',
                },
                {
                    text: 'Макияж свадебный',
                    value: 'wedding_makeup_cnt_share',
                    align: 'right',
                },
                {
                    text: 'Экспресс макияж',
                    value: 'express_makeup_cnt',
                    align: 'right',
                },
                {
                    text: 'Экспресс макияж',
                    value: 'express_makeup_cnt_share',
                    align: 'right',
                },
                {
                    text: 'Прическа (общая)',
                    value: 'hairstyle_cnt',
                    align: 'right',
                },
                {
                    text: 'Прическа (общая)',
                    value: 'hairstyle_cnt_share',
                    align: 'right',
                },
                {
                    text: 'Укладка (общая + локоны)',
                    value: 'styling_cnt',
                    align: 'right',
                },
                {
                    text: 'Укладка (общая + локоны)',
                    value: 'styling_cnt_share',
                    align: 'right',
                },
                {
                    text: 'Комплексы',
                    value: 'complex_hair_makeup_cnt',
                    align: 'right',
                },
                {
                    text: 'Комплексы',
                    value: 'complex_hair_makeup_cnt_share',
                    align: 'right',
                },
                {
                    text: 'Стрижка женская',
                    value: 'female_haircut_sum',
                    align: 'right',
                },
                {
                    text: 'Стрижка женская',
                    value: 'female_haircut_sum_share',
                    align: 'right',
                },
                {
                    text: 'Стрижка мужская + детская',
                    value: 'male_kids_haircut_sum',
                    align: 'right',
                },
                {
                    text: 'Стрижка мужская + детская',
                    value: 'male_kids_haircut_sum_share',
                    align: 'right',
                },
                {
                    text: 'Окрашивание (общее)',
                    value: 'coloring_sum',
                    align: 'right',
                },
                {
                    text: 'Окрашивание (общее)',
                    value: 'coloring_sum_share',
                    align: 'right',
                },
                {
                    text: 'Мелирование/тонирование',
                    value: 'highlight_tone_sum',
                    align: 'right',
                },
                {
                    text: 'Мелирование/тонирование',
                    value: 'highlight_tone_sum_share',
                    align: 'right',
                },
                {
                    text: 'Уход',
                    value: 'treatments_sum',
                    align: 'right',
                },
                {
                    text: 'Уход',
                    value: 'treatments_sum_share',
                    align: 'right',
                },
                {
                    text: 'Макияж (общий)',
                    value: 'makeup_sum',
                    align: 'right',
                },
                {
                    text: 'Макияж (общий)',
                    value: 'makeup_sum_share',
                    align: 'right',
                },
                {
                    text: 'Макияж дневной',
                    value: 'day_makeup_sum',
                    align: 'right',
                },
                {
                    text: 'Макияж дневной',
                    value: 'day_makeup_sum_share',
                    align: 'right',
                },
                {
                    text: 'Макияж вечерний',
                    value: 'evening_makeup_sum',
                    align: 'right',
                },
                {
                    text: 'Макияж вечерний',
                    value: 'evening_makeup_sum_share',
                    align: 'right',
                },
                {
                    text: 'Макияж свадебный',
                    value: 'wedding_makeup_sum',
                    align: 'right',
                },
                {
                    text: 'Макияж свадебный',
                    value: 'wedding_makeup_sum_share',
                    align: 'right',
                },
                {
                    text: 'Экспресс макияж',
                    value: 'express_makeup_sum',
                    align: 'right',
                },
                {
                    text: 'Экспресс макияж',
                    value: 'express_makeup_sum_share',
                    align: 'right',
                },
                {
                    text: 'Прическа (общая)',
                    value: 'hairstyle_sum',
                    align: 'right',
                },
                {
                    text: 'Прическа (общая)',
                    value: 'hairstyle_sum_share',
                    align: 'right',
                },
                {
                    text: 'Укладка (общая + локоны)',
                    value: 'styling_sum',
                    align: 'right',
                },
                {
                    text: 'Укладка (общая + локоны)',
                    value: 'styling_sum_share',
                    align: 'right',
                },
                {
                    text: 'Комплексы',
                    value: 'complex_hair_makeup_sum',
                    align: 'right',
                },
                {
                    text: 'Комплексы',
                    value: 'complex_hair_makeup_sum_share',
                    align: 'right',
                },
                {
                    text: 'Стрижка женская',
                    value: 'female_haircut_score',
                    align: 'right',
                },
                {
                    text: 'Стрижка мужская + детская',
                    value: 'male_kids_haircut_score',
                    align: 'right',
                },
                {
                    text: 'Окрашивание (общее)',
                    value: 'coloring_score',
                    align: 'right',
                },
                {
                    text: 'Мелирование/тонирование',
                    value: 'highlight_tone_score',
                    align: 'right',
                },
                {
                    text: 'Уход',
                    value: 'treatments_score',
                    align: 'right',
                },
                {
                    text: 'Макияж (общий)',
                    value: 'makeup_score',
                    align: 'right',
                },
                {
                    text: 'Макияж дневной',
                    value: 'day_makeup_score',
                    align: 'right',
                },
                {
                    text: 'Макияж вечерний',
                    value: 'evening_makeup_score',
                    align: 'right',
                },
                {
                    text: 'Макияж свадебный',
                    value: 'wedding_makeup_score',
                    align: 'right',
                },
                {
                    text: 'Экспресс макияж',
                    value: 'express_makeup_score',
                    align: 'right',
                },
                {
                    text: 'Прическа (общая)',
                    value: 'hairstyle_score',
                    align: 'right',
                },
                {
                    text: 'Укладка (общая + локоны)',
                    value: 'styling_score',
                    align: 'right',
                },
                {
                    text: 'Комплексы',
                    value: 'complex_hair_makeup_score',
                    align: 'right',
                },
                {
                    text: 'Стрижка женская',
                    value: 'female_haircut_first_rec',
                    align: 'right',
                },
                {
                    text: 'Стрижка мужская + детская',
                    value: 'male_kids_haircut_first_rec',
                    align: 'right',
                },
                {
                    text: 'Окрашивание (общее)',
                    value: 'coloring_first_rec',
                    align: 'right',
                },
                {
                    text: 'Мелирование/тонирование',
                    value: 'highlight_tone_first_rec',
                    align: 'right',
                },
                {
                    text: 'Уход',
                    value: 'treatments_first_rec',
                    align: 'right',
                },
                {
                    text: 'Макияж (общий)',
                    value: 'makeup_first_rec',
                    align: 'right',
                },
                {
                    text: 'Макияж дневной',
                    value: 'day_makeup_first_rec',
                    align: 'right',
                },
                {
                    text: 'Макияж вечерний',
                    value: 'evening_makeup_first_rec',
                    align: 'right',
                },
                {
                    text: 'Макияж свадебный',
                    value: 'wedding_makeup_first_rec',
                    align: 'right',
                },
                {
                    text: 'Экспресс макияж',
                    value: 'express_makeup_first_rec',
                    align: 'right',
                },
                {
                    text: 'Прическа (общая)',
                    value: 'hairstyle_first_rec',
                    align: 'right',
                },
                {
                    text: 'Укладка (общая + локоны)',
                    value: 'styling_first_rec',
                    align: 'right',
                },
                {
                    text: 'Комплексы',
                    value: 'complex_hair_makeup_first_rec',
                    align: 'right',
                },
            ]
        },
        massageSalonTableHeaders4H(): TableHeader[] {
            return [
                {
                    text: 'Имя сотрудника',
                    value: 'staff_name',
                    width: '100px',
                    align: 'left'
                },
                {
                    text: 'Массаж классический (общий)',
                    value: 'classic_massage_cnt',
                    align: 'right',
                },
                {
                    text: 'Массаж классический (общий)',
                    value: 'classic_massage_cnt_share',
                    align: 'right',
                },
                {
                    text: 'Массаж лимфодренажный',
                    value: 'lymphatic_massage_cnt',
                    align: 'right',
                },
                {
                    text: 'Массаж лимфодренажный',
                    value: 'lymphatic_massage_cnt_share',
                    align: 'right',
                },
                {
                    text: 'Массаж антицеллюлитный',
                    value: 'anticellulite_massage_cnt',
                    align: 'right',
                },
                {
                    text: 'Массаж антицеллюлитный',
                    value: 'anticellulite_massage_cnt_share',
                    align: 'right',
                },
                {
                    text: 'Массаж стоп и голеней (+ноги)',
                    value: 'legs_massage_cnt',
                    align: 'right',
                },
                {
                    text: 'Массаж стоп и голеней (+ноги)',
                    value: 'legs_massage_cnt_share',
                    align: 'right',
                },
                {
                    text: 'Массаж спины',
                    value: 'back_massage_cnt',
                    align: 'right',
                },
                {
                    text: 'Массаж спины',
                    value: 'back_massage_cnt_share',
                    align: 'right',
                },
                {
                    text: 'Массаж шейно-воротниковой зоны',
                    value: 'neck_massage_cnt',
                    align: 'right',
                },
                {
                    text: 'Массаж шейно-воротниковой зоны',
                    value: 'neck_massage_cnt_share',
                    align: 'right',
                },
                {
                    text: 'Массаж лица',
                    value: 'face_massage_cnt',
                    align: 'right',
                },
                {
                    text: 'Массаж лица',
                    value: 'face_massage_cnt_share',
                    align: 'right',
                },
                {
                    text: 'Массаж энергетический',
                    value: 'energy_massage_cnt',
                    align: 'right',
                },
                {
                    text: 'Массаж энергетический',
                    value: 'energy_massage_cnt_share',
                    align: 'right',
                },
                {
                    text: 'Массаж топ-мастер',
                    value: 'top_master_massage_cnt',
                    align: 'right',
                },
                {
                    text: 'Массаж топ-мастер',
                    value: 'top_master_massage_cnt_share',
                    align: 'right',
                },
                {
                    text: 'Прочее',
                    value: 'other_massage_cnt',
                    align: 'right',
                },
                {
                    text: 'Прочее',
                    value: 'other_massage_cnt_share',
                    align: 'right',
                },
                {
                    text: 'Массаж классический (общий)',
                    value: 'classic_massage_sum',
                    align: 'right',
                },
                {
                    text: 'Массаж классический (общий)',
                    value: 'classic_massage_sum_share',
                    align: 'right',
                },
                {
                    text: 'Массаж лимфодренажный',
                    value: 'lymphatic_massage_sum',
                    align: 'right',
                },
                {
                    text: 'Массаж лимфодренажный',
                    value: 'lymphatic_massage_sum_share',
                    align: 'right',
                },
                {
                    text: 'Массаж антицеллюлитный',
                    value: 'anticellulite_massage_sum',
                    align: 'right',
                },
                {
                    text: 'Массаж антицеллюлитный',
                    value: 'anticellulite_massage_sum_share',
                    align: 'right',
                },
                {
                    text: 'Массаж стоп и голеней (+ноги)',
                    value: 'legs_massage_sum',
                    align: 'right',
                },
                {
                    text: 'Массаж стоп и голеней (+ноги)',
                    value: 'legs_massage_sum_share',
                    align: 'right',
                },
                {
                    text: 'Массаж спины',
                    value: 'back_massage_sum',
                    align: 'right',
                },
                {
                    text: 'Массаж спины',
                    value: 'back_massage_sum_share',
                    align: 'right',
                },
                {
                    text: 'Массаж шейно-воротниковой зоны',
                    value: 'neck_massage_sum',
                    align: 'right',
                },
                {
                    text: 'Массаж шейно-воротниковой зоны',
                    value: 'neck_massage_sum_share',
                    align: 'right',
                },
                {
                    text: 'Массаж лица',
                    value: 'face_massage_sum',
                    align: 'right',
                },
                {
                    text: 'Массаж лица',
                    value: 'face_massage_sum_share',
                    align: 'right',
                },
                {
                    text: 'Массаж энергетический',
                    value: 'energy_massage_sum',
                    align: 'right',
                },
                {
                    text: 'Массаж энергетический',
                    value: 'energy_massage_sum_share',
                    align: 'right',
                },
                {
                    text: 'Массаж топ-мастер',
                    value: 'top_master_massage_sum',
                    align: 'right',
                },
                {
                    text: 'Массаж топ-мастер',
                    value: 'top_master_massage_sum_share',
                    align: 'right',
                },
                {
                    text: 'Прочее',
                    value: 'other_massage_sum',
                    align: 'right',
                },
                {
                    text: 'Прочее',
                    value: 'other_massage_sum_share',
                    align: 'right',
                },
                {
                    text: 'Массаж классический (общий)',
                    value: 'classic_massage_score',
                    align: 'right',
                },
                {
                    text: 'Массаж лимфодренажный',
                    value: 'lymphatic_massage_score',
                    align: 'right',
                },
                {
                    text: 'Массаж антицеллюлитный',
                    value: 'anticellulite_massage_score',
                    align: 'right',
                },
                {
                    text: 'Массаж стоп и голеней (+ноги)',
                    value: 'legs_massage_score',
                    align: 'right',
                },
                {
                    text: 'Массаж спины',
                    value: 'back_massage_score',
                    align: 'right',
                },
                {
                    text: 'Массаж шейно-воротниковой зоны',
                    value: 'neck_massage_score',
                    align: 'right',
                },
                {
                    text: 'Массаж лица',
                    value: 'face_massage_score',
                    align: 'right',
                },
                {
                    text: 'Массаж энергетический',
                    value: 'energy_massage_score',
                    align: 'right',
                },
                {
                    text: 'Массаж топ-мастер',
                    value: 'top_master_massage_score',
                    align: 'right',
                },
                {
                    text: 'Прочее',
                    value: 'other_massage_score',
                    align: 'right',
                },
                {
                    text: 'Массаж классический (общий)',
                    value: 'classic_massage_first_rec',
                    align: 'right',
                },
                {
                    text: 'Массаж лимфодренажный',
                    value: 'lymphatic_massage_first_rec',
                    align: 'right',
                },
                {
                    text: 'Массаж антицеллюлитный',
                    value: 'anticellulite_massage_first_rec',
                    align: 'right',
                },
                {
                    text: 'Массаж стоп и голеней (+ноги)',
                    value: 'legs_massage_first_rec',
                    align: 'right',
                },
                {
                    text: 'Массаж спины',
                    value: 'back_massage_first_rec',
                    align: 'right',
                },
                {
                    text: 'Массаж шейно-воротниковой зоны',
                    value: 'neck_massage_first_rec',
                    align: 'right',
                },
                {
                    text: 'Массаж лица',
                    value: 'face_massage_first_rec',
                    align: 'right',
                },
                {
                    text: 'Массаж энергетический',
                    value: 'energy_massage_first_rec',
                    align: 'right',
                },
                {
                    text: 'Массаж топ-мастер',
                    value: 'top_master_massage_first_rec',
                    align: 'right',
                },
                {
                    text: 'Прочее',
                    value: 'other_massage_first_rec',
                    align: 'right',
                },
            ]
        },
        epilationSalonTableHeaders4H(): TableHeader[] {
            return [
                {
                    text: 'Имя сотрудника',
                    value: 'staff_name',
                    width: '100px',
                    align: 'left'
                },
                {
                    text: 'Подмышечные впадины',
                    value: 'armpits_cnt',
                    align: 'right',
                },
                {
                    text: 'Подмышечные впадины',
                    value: 'armpits_cnt_share',
                    align: 'right',
                },
                {
                    text: 'Бикини',
                    value: 'bikini_cnt',
                    align: 'right',
                },
                {
                    text: 'Бикини',
                    value: 'bikini_cnt_share',
                    align: 'right',
                },
                {
                    text: 'Глубокое бикини',
                    value: 'deep_bikini_cnt',
                    align: 'right',
                },
                {
                    text: 'Глубокое бикини',
                    value: 'deep_bikini_cnt_share',
                    align: 'right',
                },
                {
                    text: 'Ноги',
                    value: 'legs_cnt',
                    align: 'right',
                },
                {
                    text: 'Ноги',
                    value: 'legs_cnt_share',
                    align: 'right',
                },
                {
                    text: 'Живот',
                    value: 'stomach_cnt',
                    align: 'right',
                },
                {
                    text: 'Живот',
                    value: 'stomach_cnt_share',
                    align: 'right',
                },
                {
                    text: 'Руки',
                    value: 'arms_cnt',
                    align: 'right',
                },
                {
                    text: 'Руки',
                    value: 'arms_cnt_share',
                    align: 'right',
                },
                {
                    text: 'Обезболивающее средство',
                    value: 'anesthetic_cnt',
                    align: 'right',
                },
                {
                    text: 'Обезболивающее средство',
                    value: 'anesthetic_cnt_share',
                    align: 'right',
                },
                {
                    text: 'Мужская эпиляция',
                    value: 'male_epilation_cnt',
                    align: 'right',
                },
                {
                    text: 'Мужская эпиляция',
                    value: 'male_epilation_cnt_share',
                    align: 'right',
                },
                {
                    text: 'Прочее',
                    value: 'other_epilation_cnt',
                    align: 'right',
                },
                {
                    text: 'Прочее',
                    value: 'other_epilation_cnt_share',
                    align: 'right',
                },
                {
                    text: 'Подмышечные впадины',
                    value: 'armpits_sum',
                    align: 'right',
                },
                {
                    text: 'Подмышечные впадины',
                    value: 'armpits_sum_share',
                    align: 'right',
                },
                {
                    text: 'Бикини',
                    value: 'bikini_sum',
                    align: 'right',
                },
                {
                    text: 'Бикини',
                    value: 'bikini_sum_share',
                    align: 'right',
                },
                {
                    text: 'Глубокое бикини',
                    value: 'deep_bikini_sum',
                    align: 'right',
                },
                {
                    text: 'Глубокое бикини',
                    value: 'deep_bikini_sum_share',
                    align: 'right',
                },
                {
                    text: 'Ноги',
                    value: 'legs_sum',
                    align: 'right',
                },
                {
                    text: 'Ноги',
                    value: 'legs_sum_share',
                    align: 'right',
                },
                {
                    text: 'Живот',
                    value: 'stomach_sum',
                    align: 'right',
                },
                {
                    text: 'Живот',
                    value: 'stomach_sum_share',
                    align: 'right',
                },
                {
                    text: 'Руки',
                    value: 'arms_sum',
                    align: 'right',
                },
                {
                    text: 'Руки',
                    value: 'arms_sum_share',
                    align: 'right',
                },
                {
                    text: 'Обезболивающее средство',
                    value: 'anesthetic_sum',
                    align: 'right',
                },
                {
                    text: 'Обезболивающее средство',
                    value: 'anesthetic_sum_share',
                    align: 'right',
                },
                {
                    text: 'Мужская эпиляция',
                    value: 'male_epilation_sum',
                    align: 'right',
                },
                {
                    text: 'Мужская эпиляция',
                    value: 'male_epilation_sum_share',
                    align: 'right',
                },
                {
                    text: 'Прочее',
                    value: 'other_epilation_sum',
                    align: 'right',
                },
                {
                    text: 'Прочее',
                    value: 'other_epilation_sum_share',
                    align: 'right',
                },
                {
                    text: 'Подмышечные впадины',
                    value: 'armpits_score',
                    align: 'right',
                },
                {
                    text: 'Бикини',
                    value: 'bikini_score',
                    align: 'right',
                },
                {
                    text: 'Глубокое бикини',
                    value: 'deep_bikini_score',
                    align: 'right',
                },
                {
                    text: 'Ноги',
                    value: 'legs_score',
                    align: 'right',
                },
                {
                    text: 'Живот',
                    value: 'stomach_score',
                    align: 'right',
                },
                {
                    text: 'Руки',
                    value: 'arms_score',
                    align: 'right',
                },
                {
                    text: 'Обезболивающее средство',
                    value: 'anesthetic_score',
                    align: 'right',
                },
                {
                    text: 'Мужская эпиляция',
                    value: 'male_epilation_score',
                    align: 'right',
                },
                {
                    text: 'Прочее',
                    value: 'other_epilation_score',
                    align: 'right',
                },
                {
                    text: 'Подмышечные впадины',
                    value: 'armpits_first_rec',
                    align: 'right',
                },
                {
                    text: 'Бикини',
                    value: 'bikini_first_rec',
                    align: 'right',
                },
                {
                    text: 'Глубокое бикини',
                    value: 'deep_bikini_first_rec',
                    align: 'right',
                },
                {
                    text: 'Ноги',
                    value: 'legs_first_rec',
                    align: 'right',
                },
                {
                    text: 'Живот',
                    value: 'stomach_first_rec',
                    align: 'right',
                },
                {
                    text: 'Руки',
                    value: 'arms_first_rec',
                    align: 'right',
                },
                {
                    text: 'Обезболивающее средство',
                    value: 'anesthetic_first_rec',
                    align: 'right',
                },
                {
                    text: 'Мужская эпиляция',
                    value: 'male_epilation_first_rec',
                    align: 'right',
                },
                {
                    text: 'Прочее',
                    value: 'other_epilation_first_rec',
                    align: 'right',
                },
            ]
        }
    },
    methods: {
        toSnakeCaseRecord(record: Record<string, any>): Record<string, any> {
            return Object.keys(record || {}).reduce((acc: Record<string, any>, key: string) => {
                const snakeKey = key.replace(/([A-Z])/g, '_$1').toLowerCase()
                acc[snakeKey] = (record as any)[key]
                return acc
            }, {})
        },
        normalize4hRows(rows: any[]): any[] {
            if (!Array.isArray(rows)) return []
            return rows.map((row) => this.toSnakeCaseRecord(row))
        },
        setDefaultDates() {
            this.dates = [
                moment()
                    .subtract(30, 'days')
                    .format(this.datePickerDateFormat),
                moment()
                    .subtract(0, 'days')
                    .format(this.datePickerDateFormat),
            ]
        },
        async initData(init: boolean, includeFileLink: boolean) {
            this.loading = true
            if (init){
                this.setDefaultDates()
            }
            const filters = {
                minDate: this.dates[0],
                maxDate: this.dates.length === 2 ? this.dates[1] : this.dates[0],
                filialList: this.filialIds
            }
            try {
                const {data, error} = await getStaffTrajectoryReport({
                    projectId: this.project.id,
                    filters,
                    includeFileLink,
                    for4hands: this.is4Hands
                })

                const allTableData = data?.reportData || {}

                if (this.is4Hands) {
                    // Данные для 4hands
                    this.hairdressingSalonTableData = this.normalize4hRows(allTableData.hairMakeup4hData || data?.hairMakeup4hData || [])
                    this.manicureSalonTableData = this.normalize4hRows(allTableData.nails4hData || data?.nails4hData || [])
                    this.cosmetologySalonTableData = this.normalize4hRows(allTableData.lashBrow4hData || data?.lashBrow4hData || [])
                    this.massageSalonTableData = this.normalize4hRows(allTableData.massage4hData || data?.massage4hData || [])
                    this.epilationSalonTableData = this.normalize4hRows(allTableData.epilation4hData || data?.epilation4hData || [])
                } else {
                    // Данные для обычных отчетов
                    this.hairdressingSalonTableData = allTableData.hairdresserDataShort || []
                    this.cosmetologySalonTableData = allTableData.cosmDataShort || []
                    this.manicureSalonTableData = allTableData.nailsDataShort || []
                }

                this.originalData.hairdressingSalon = [...this.hairdressingSalonTableData]
                this.originalData.cosmetologySalon = [...this.cosmetologySalonTableData]
                this.originalData.manicureSalon = [...this.manicureSalonTableData]
                this.originalData.massageSalon = [...this.massageSalonTableData]
                this.originalData.epilationSalon = [...this.epilationSalonTableData]

                this.sortState = {
                    hairdressingSalon: { column: '', direction: 'none' },
                    cosmetologySalon: { column: '', direction: 'none' },
                    manicureSalon: { column: '', direction: 'none' },
                    massageSalon: { column: '', direction: 'none' },
                    epilationSalon: { column: '', direction: 'none' }
                }

                if (data?.fileSrc && includeFileLink) {
                    downloadFile(data.fileSrc, '')
                }
                if(error) {
                    this.$store.dispatch('callNotify', `Произошла ошибка`)
                }
            } finally {
                this.loading = false
            }
            
        },
        sortTable(tableName: string, column: string) {
            const currentSort = this.sortState[tableName]
            
            if (currentSort.column === column) {
                if (currentSort.direction === 'asc') {
                    currentSort.direction = 'desc'
                } else if (currentSort.direction === 'desc') {
                    currentSort.direction = 'none'
                    currentSort.column = ''
                } else {
                    currentSort.direction = 'asc'
                }
            } else {
                currentSort.column = column
                currentSort.direction = 'asc'
            }

            if (currentSort.direction === 'none') {
                this.restoreOriginalData(tableName)
            } else {
                this.sortData(tableName, column, currentSort.direction)
            }
        },
        
        sortData(tableName: string, column: string, direction: 'asc' | 'desc') {
            const dataKey = `${tableName}TableData`
            const data = this[dataKey]
            
            if (!data || !Array.isArray(data)) return
            
            const sortedData = [...data].sort((a, b) => {
                let aVal = a[column]
                let bVal = b[column]
                
                const parseNum = (v: any): number | null => {
                    if (v === null || v === undefined) return null
                    if (typeof v === 'number') return v
                    if (typeof v === 'string') {
                        const normalized = v.replace(',', '.')
                        const num = Number(normalized)
                        return isNaN(num) ? null : num
                    }
                    return null
                }

                const aNum = parseNum(aVal)
                const bNum = parseNum(bVal)
                if (aNum !== null && bNum !== null) {
                    return direction === 'asc' ? aNum - bNum : bNum - aNum
                }

                if (typeof aVal === 'number' && typeof bVal === 'number') {
                    return direction === 'asc' ? aVal - bVal : bVal - aVal
                }

                if (typeof aVal === 'string' && typeof bVal === 'string') {
                    aVal = aVal.toLowerCase()
                    bVal = bVal.toLowerCase()
                    if (direction === 'asc') {
                        return aVal.localeCompare(bVal)
                    } else {
                        return bVal.localeCompare(aVal)
                    }
                }
                
                return 0
            })
            
            this[dataKey] = sortedData
        },
        
        restoreOriginalData(tableName: string) {
            const dataKey = `${tableName}TableData`
            const originalKey = tableName
            
            this[dataKey] = [...(this.originalData[originalKey] || [])]
        },
        
        getSortIcon(tableName: string, column: string) {
            const sortState = this.sortState[tableName]
            if (sortState.column === column) {
                return sortState.direction === 'asc' ? 'mdi-arrow-up' : 'mdi-arrow-down'
            }
            return 'mdi-arrow-up'
        },
        
        getBcgColorClass(value: number, tableName: string, colName: string): string {
            const tableDataCopy = this[tableName]
            
            const values = tableDataCopy.map(item => item[colName]).filter(val => val !== null && val !== undefined)
            
            if (values.length === 0) return 'transparent'
            
            const minValue = Math.min(...values)
            const maxValue = Math.max(...values)
            const range = maxValue - minValue
            
            if (range === 0) return 'transparent'
            
            const midPoint = (minValue + maxValue) / 2
            const distanceFromMid = Math.abs(value - midPoint)
            const maxDistance = range / 2
            
            if (distanceFromMid / maxDistance < 0.1) {
                return 'transparent'
            }
            
            const intensity = Math.min(distanceFromMid / maxDistance, 1)
            
            if (value > midPoint) {
                return this.generateGradientColor(intensity, 'green')
            } else {
                return this.generateGradientColor(intensity, 'red')
            }
        },
        
        generateGradientColor(intensity: number, type: 'green' | 'red'): string {
            const greenStart = '#eaf6f1' // light green
            const greenEnd = '#57bb8a' // dark green
            const redStart = '#fcedec' // light red
            const redEnd = '#e67c73' // dark red
            
            if (type === 'green') {
                return this.interpolateColor(greenStart, greenEnd, intensity)
            } else {
                return this.interpolateColor(redStart, redEnd, intensity)
            }
        },
        
        interpolateColor(color1: string, color2: string, factor: number): string {
            const hex1 = color1.replace('#', '')
            const hex2 = color2.replace('#', '')
            
            const r1 = parseInt(hex1.substring(0, 2), 16)
            const g1 = parseInt(hex1.substring(2, 4), 16)
            const b1 = parseInt(hex1.substring(4, 6), 16)
            
            const r2 = parseInt(hex2.substring(0, 2), 16)
            const g2 = parseInt(hex2.substring(2, 4), 16)
            const b2 = parseInt(hex2.substring(4, 6), 16)
            
            const r = Math.round(r1 + (r2 - r1) * factor)
            const g = Math.round(g1 + (g2 - g1) * factor)
            const b = Math.round(b1 + (b2 - b1) * factor)
            
            return `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`
        },
        
        randomInt(min: number, max: number): number {
            return Math.floor(Math.random() * (max - min + 1)) + min
        },
        
        randomRating(min: number = 3.8, max: number = 5.0): string {
            const value = Math.min(max, Math.max(min, Math.random() * (max - min) + min))
            return value.toFixed(1).replace('.', ',')
        },

        parseRatingValue(value: string | number): number | null {
            if (value === null || value === undefined) return null
            if (typeof value === 'number') return value
            const normalized = String(value).replace(',', '.')
            const num = Number(normalized)
            return isNaN(num) ? null : num
        },
        formatFirstRecDate(value: any): string {
            if (value === null || value === undefined) return '-'
            if (value === '-') return '-'
            const asString = String(value)
            const parsed = moment(asString, 'YYYY-MM-DD', true)
            return parsed.isValid() ? parsed.format('DD MMM YYYY') : asString
        },

        getRatingColor(value: string | number): string {
            const parsed = this.parseRatingValue(value)
            if (parsed === null) return 'transparent'
            return parsed >= 0.1 && parsed <= 4.5 ? '#ec9c96' : 'transparent'
        },
    },
    async mounted() {
        if(!this.isHrProject) {
            this.$router.push(`/project/${this.project.id}`)
        }
        await this.initData(true, false)
        
        this.$nextTick(() => {
            this.originalData.hairdressingSalon = [...this.hairdressingSalonTableData]
            this.originalData.cosmetologySalon = [...this.cosmetologySalonTableData]
            this.originalData.manicureSalon = [...this.manicureSalonTableData]
        })
    },
    setup(props, { emit }) {},
})
